import { useState } from 'react';
import { Common, color, Header } from '@stockbit/ui';
import { useRouter } from 'next/router';

import { SITE_URL } from 'constants/api';
import If from 'global/ErrorHandlers/If';
import {
  SWITCH_TO_REAL_ACCOUNT_SOURCE,
  virtualTradingAction,
} from 'lib/analytics/virtualTrading';
import { sanitizeURL } from 'utils/general';
import useAuthModalStore from 'features/securities/AuthModal/store';
import useSecuritiesCredentialStore from 'global/Securities/stores/useSecuritiesCredentialStore';
import { AuthModalStatus } from 'features/securities/AuthModal/types';
import useSearchCompanyModalStore from 'global/Securities/stores/useSearchCompanyModalStore';
import useOpeningAccountStore from 'global/Securities/stores/useOpeningAccountStore';
import virtualTradingMenu from './virtualTradingMenu';
import ButtonTradingOutline from './ButtonTradingOutline';
import TradingMenuItem from './TradingMenuItem';
import { SwitchButton } from '../styled';

import {
  PopoverContainerBlock,
  TradeAreaContent,
  TradingMenuTitle,
} from './styled';

const { Button, Flex, IconWrapper, Iconmoon, Text } = Common;
const Popover = Header.PopoverCustom;

const Web = ({ minimize, ...otherProps }) => {
  const router = useRouter();
  const showAuthModal = useAuthModalStore((state) => state.showModal);
  const showSearchCompanyModal = useSearchCompanyModalStore((s) => s.showModal);
  const oaStatus = useOpeningAccountStore((s) => s.status);
  const isStockbitSecuritas = oaStatus === AuthModalStatus.COMPLETED;
  const handleNavigation = (label, link) => {
    router.push(sanitizeURL(link));
  };
  const [isVisible, setIsVisible] = useState<boolean>(false);

  const analyticsNavigate = (label) => {
    switch (label) {
      case 'Orderbook':
        virtualTradingAction.onVirtualTradingOrderbookSelected();
        break;
      case 'Portfolio':
        virtualTradingAction.onVirtualTradingPortfolioSelected();
        break;
      case 'Order':
        virtualTradingAction.onVirtualTradingOrderSelected();
        break;

      default:
        break;
    }
  };

  const handleSwitchReal = async () => {
    await useSecuritiesCredentialStore.getState().loadLogout();

    if (['/trade/portfolio/', '/trade/order/'].includes(router.asPath)) {
      await router.push('/stream');
    }

    showAuthModal();
  };

  return (
    <Popover
      componentTrigger={(isButtonActive) => (
        <ButtonTradingOutline
          {...otherProps}
          data-cy="virtual-trading-btn"
          isActive={isButtonActive}
          minimize={minimize}
          label="Virtual Area"
          imageSrc={`${SITE_URL}/img/favicon.svg`}
          onChangeVisibility={(active) => setIsVisible(active)}
        />
      )}
    >
      <If condition={isVisible}>
        <PopoverContainerBlock>
          {virtualTradingMenu.map(({ title, subMenu }, i) => (
            <TradeAreaContent key={`trade-area-content-${i + 1}`}>
              <TradingMenuTitle>
                <Text family="bold" color={color.primary}>
                  {title}
                </Text>
              </TradingMenuTitle>
              {i === 0 && (
                <div style={{ padding: '8px 16px' }}>
                  <Button
                    type="primary"
                    height="32px"
                    block
                    onClick={() => {
                      showSearchCompanyModal();
                      setIsVisible(false);
                    }}
                  >
                    <Text
                      color={color.light}
                      size="14px"
                      family="bold"
                      align="center"
                    >
                      BUY
                    </Text>
                  </Button>
                </div>
              )}
              {subMenu.map(({ iconmoonWeb, label, link }, index) => (
                <TradingMenuItem
                  key={`trading-menu-item-${index + 1}`}
                  iconmoon={iconmoonWeb}
                  onClick={() => {
                    handleNavigation(label, link);
                    analyticsNavigate(label);
                  }}
                >
                  {label}
                </TradingMenuItem>
              ))}
            </TradeAreaContent>
          ))}
          <TradeAreaContent>
            <div
              data-cy="switch-to-real-account-btn"
              style={{ padding: '0px 16px' }}
              onClick={() => {
                handleSwitchReal();
                virtualTradingAction.onSwitchToRealTradingSelected(
                  SWITCH_TO_REAL_ACCOUNT_SOURCE.dropdownVirtualArea,
                  isStockbitSecuritas,
                );
              }}
              aria-hidden
            >
              <SwitchButton height="32px" block>
                <Flex>
                  <Text color={color.primary} size="14px" padding="0 8px">
                    Switch to Real Account
                  </Text>
                  <IconWrapper size="18px">
                    <Iconmoon
                      className="icon-tradesmall_switch"
                      size="12px"
                      color={color.primary}
                    />
                  </IconWrapper>
                </Flex>
              </SwitchButton>
            </div>
          </TradeAreaContent>
        </PopoverContainerBlock>
      </If>
    </Popover>
  );
};

export default Web;
